import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getClaimFormData,
  getClaimFormStatus,
} from 'legacyfrontend/redux/selectors/forms-selectors';
import {
  setClaimFormData,
  submitClaimForm,
  resetClaimForm,
} from 'legacyfrontend/redux/actions/forms-actions';
import ErrorSnackbar from '../../ErrorSnackbar';
import FormSubmissionModal from '../../FormSubmissionModal';
import { useFormToast } from 'legacyfrontend/hooks/useToast';

const FormField = memo(
  ({
    label,
    name,
    type = 'text',
    value,
    onChange,
    required = false,
    options = null,
    rows = null,
    placeholder = '',
    numericOnly = false,
    ...props
  }) => {
    const handleChange = useCallback(
      (event) => {
        let { value: inputValue } = event.target;

        if (numericOnly && inputValue) {
          if (name.includes('telefono') || name.includes('Telefono')) {
            inputValue = inputValue.replace(/[^0-9\s\-+()]/g, '');
          } else {
            inputValue = inputValue.replace(/[^0-9]/g, '');
          }
          event.target.value = inputValue;
        }

        onChange(event);
      },
      [onChange, numericOnly, name]
    );

    const fieldProps = useMemo(
      () => ({
        name,
        value: value || '',
        onChange: handleChange,
        className:
          'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
        required,
        placeholder,
        ...props,
      }),
      [name, value, handleChange, required, placeholder, props]
    );

    return (
      <div className='mb-4'>
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          {label} {required && <span className='text-red-500'>(*)</span>}
        </label>
        {type === 'select' && options ? (
          <select {...fieldProps}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea {...fieldProps} rows={rows || 4} />
        ) : type === 'checkbox' ? (
          <input
            {...fieldProps}
            type='checkbox'
            checked={value || false}
            className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
          />
        ) : (
          <input {...fieldProps} type={type} />
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

const DOCUMENT_TYPE_OPTIONS = [
  { value: '', label: '--- Seleccionar ---' },
  { value: 'DNI', label: 'DNI' },
  { value: 'C.E.', label: 'C.E.' },
];

const CLAIM_TYPE_OPTIONS = [
  { value: 'Reclamo', label: 'Reclamo' },
  { value: 'Queja', label: 'Queja' },
];

const CONTRACTED_GOOD_OPTIONS = [
  { value: 'Producto', label: 'Producto' },
  { value: 'Servicio', label: 'Servicio' },
];

const MINOR_AGE_OPTIONS = [
  { value: '', label: '--- Seleccionar ---' },
  { value: 'Si', label: 'Sí' },
  { value: 'No', label: 'No' },
];

function FormularioReclamo() {
  const history = useHistory();
  const dispatch = useDispatch();

  const { toastState, hideToast, showValidationError, showSubmissionSuccess, showSubmissionError } =
    useFormToast();

  const applicationState = useSelector((state) => state.applicationState);
  const claimForm = useSelector(getClaimFormData);
  const claimSubmissionStatus = useSelector(getClaimFormStatus);

  const [modalOpen, setModalOpen] = useState(false);

  const initialFormData = useMemo(() => {
    if (!applicationState) return {};

    return {
      agenciaId: applicationState.applicationId,
      nombreAgencia: applicationState.nombre,
      emailAgencia: applicationState.templateData?.header?.emailContacto || '',
      paisId: applicationState.paisId,
      tipoAplicacionId: applicationState.tipoAplicacionId,
      estado: { id: 1 },

      tipoDocumento: '',
      numeroDocumento: '',
      nombre: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      domicilio: '',
      departamento: '',
      provincia: '',
      distrito: '',
      telefonoFijo: '',
      telefonoCelular: '',
      email: '',
      bienContratado: 'Producto',
      montoReclamado: '',
      descripcion: '',
      tipoReclamo: 'Reclamo',
      detalleReclamo: '',
      pedido: '',
      menorEdad: '',
      tipoDocumentoApoderado: '',
      documentoApoderado: '',
      nombreApoderado: '',
      apellidoPaternoApoderado: '',
      apellidoMaternoApoderado: '',
      telefonoFijoApoderado: '',
      telefonoCelularApoderado: '',
      emailApoderado: '',
    };
  }, [applicationState]);

  useEffect(() => {
    if (Object.keys(initialFormData).length > 0 && !claimForm.agenciaId) {
      dispatch(setClaimFormData(initialFormData));
    }
  }, [initialFormData, claimForm.agenciaId, dispatch]);

  useEffect(() => {
    if (claimSubmissionStatus === 'success') {
      showSubmissionSuccess();
      const timeoutId = setTimeout(() => {
        dispatch(resetClaimForm());
        history.push('/');
      }, 3000);

      return () => clearTimeout(timeoutId);
    } else if (claimSubmissionStatus === 'error') {
      showSubmissionError();
      setModalOpen(true);
    }
  }, [claimSubmissionStatus, dispatch, history]);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value, type, checked } = event.target;
      const fieldValue = type === 'checkbox' ? checked : value;
      dispatch(setClaimFormData({ [name]: fieldValue }));
    },
    [dispatch]
  );

  const isFormValid = useMemo(() => {
    const requiredFields = [
      'tipoDocumento',
      'numeroDocumento',
      'nombre',
      'domicilio',
      'telefonoCelular',
      'email',
      'descripcion',
      'detalleReclamo',
    ];

    return requiredFields.every(
      (field) => claimForm[field] && claimForm[field].toString().trim() !== ''
    );
  }, [claimForm]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!isFormValid) {
        showValidationError();
        return;
      }

      dispatch(submitClaimForm(claimForm));
    },
    [isFormValid, showValidationError, dispatch, claimForm]
  );

  const handleModalClose = useCallback(() => {
    if (claimSubmissionStatus !== 'sending') {
      setModalOpen(false);
      if (claimSubmissionStatus === 'error') {
        dispatch(resetClaimForm());
      }
    }
  }, [claimSubmissionStatus, dispatch]);

  const handleRetry = useCallback(() => {
    setModalOpen(false);
    dispatch(submitClaimForm(claimForm));
  }, [dispatch, claimForm]);

  const handleCancel = useCallback(() => {
    history.push('/');
  }, [history]);

  return (
    <div className='min-h-screen py-8'>
      <div className='max-w-6xl mx-auto px-4'>
        <div className='bg-white shadow-lg rounded-lg p-8 relative'>
          {/* Overlay de carga */}
          {claimSubmissionStatus === 'sending' && (
            <div className='absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4'></div>
                <p className='text-lg font-semibold text-gray-700'>Enviando reclamo...</p>
                <p className='text-sm text-gray-500'>Por favor espera un momento</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Header con información legal exacta */}
            <div className='mb-8'>
              <h1 className='text-3xl font-bold text-red-800 mb-4 text-center'>
                Libro de Reclamaciones
              </h1>
              <div className='bg-blue-50 border-l-4 border-blue-400 p-4 mb-6'>
                <p className='text-gray-700 mb-4'>
                  <strong>Existe un libro de reclamaciones a tu disposición.</strong> Si tienes
                  alguna queja, por favor, completa el formulario que se encuentra debajo.
                </p>
                <div className='text-sm text-gray-600 space-y-2'>
                  <p>
                    <strong>Queja:</strong> es una expresión de insatisfacción con algo, a menudo
                    relacionada con el servicio al cliente o el comportamiento de una entidad.
                  </p>
                  <p>
                    <strong>Reclamo:</strong> es una demanda o solicitud formal para obtener una
                    reparación o compensación por un daño o incumplimiento.
                  </p>
                </div>
              </div>
            </div>

            {/* SECCIÓN 1: Datos de la persona que presenta el reclamo */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                Datos de la persona que presenta el reclamo:
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                <FormField
                  label='Tipo de Documento'
                  name='tipoDocumento'
                  type='select'
                  value={claimForm.tipoDocumento}
                  onChange={handleInputChange}
                  options={DOCUMENT_TYPE_OPTIONS}
                  required
                />

                <FormField
                  label='Documento'
                  name='numeroDocumento'
                  value={claimForm.numeroDocumento}
                  onChange={handleInputChange}
                  placeholder='Número de documento'
                  required
                />

                <FormField
                  label='Nombres'
                  name='nombre'
                  value={claimForm.nombre}
                  onChange={handleInputChange}
                  placeholder='Nombres completos'
                  required
                />

                <FormField
                  label='Apellido Paterno'
                  name='apellidoPaterno'
                  value={claimForm.apellidoPaterno}
                  onChange={handleInputChange}
                  placeholder='Apellido paterno'
                />

                <FormField
                  label='Apellido Materno'
                  name='apellidoMaterno'
                  value={claimForm.apellidoMaterno}
                  onChange={handleInputChange}
                  placeholder='Apellido materno'
                />

                <FormField
                  label='Domicilio'
                  name='domicilio'
                  value={claimForm.domicilio}
                  onChange={handleInputChange}
                  placeholder='Dirección completa'
                  required
                />

                <FormField
                  label='Departamento'
                  name='departamento'
                  value={claimForm.departamento}
                  onChange={handleInputChange}
                  placeholder='Departamento'
                />

                <FormField
                  label='Provincia'
                  name='provincia'
                  value={claimForm.provincia}
                  onChange={handleInputChange}
                  placeholder='Provincia'
                />

                <FormField
                  label='Distrito'
                  name='distrito'
                  value={claimForm.distrito}
                  onChange={handleInputChange}
                  placeholder='Distrito'
                />

                <FormField
                  label='Teléfono Fijo'
                  name='telefonoFijo'
                  type='tel'
                  value={claimForm.telefonoFijo}
                  onChange={handleInputChange}
                  placeholder='Teléfono fijo'
                />

                <FormField
                  label='Teléfono Celular'
                  name='telefonoCelular'
                  type='tel'
                  value={claimForm.telefonoCelular}
                  onChange={handleInputChange}
                  placeholder='Teléfono celular'
                  required
                />

                <FormField
                  label='E-mail'
                  name='email'
                  type='email'
                  value={claimForm.email}
                  onChange={handleInputChange}
                  placeholder='correo@ejemplo.com'
                  required
                />
              </div>
            </div>

            {/* SECCIÓN 2: Información general */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                Información general:
              </h2>

              {/* Identificación del bien contratado */}
              <div className='mb-6'>
                <label className='block text-sm font-medium text-gray-700 mb-3'>
                  Identificación del bien contratado
                </label>
                <div className='flex gap-6'>
                  {CONTRACTED_GOOD_OPTIONS.map((option) => (
                    <label key={option.value} className='flex items-center'>
                      <input
                        type='radio'
                        name='bienContratado'
                        value={option.value}
                        checked={claimForm.bienContratado === option.value}
                        onChange={handleInputChange}
                        className='mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      />
                      <span className='text-sm text-gray-700'>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <FormField
                  label='Monto Reclamado (en S/.)'
                  name='montoReclamado'
                  type='number'
                  value={claimForm.montoReclamado}
                  onChange={handleInputChange}
                  placeholder='0.00'
                  step='0.01'
                  min='0'
                />

                <div className='md:col-span-2'>
                  <FormField
                    label='Descripción'
                    name='descripcion'
                    type='textarea'
                    value={claimForm.descripcion}
                    onChange={handleInputChange}
                    placeholder='Describe el producto o servicio contratado...'
                    rows={3}
                    required
                  />
                </div>
              </div>
            </div>

            {/* SECCIÓN 3: Detalle de su reclamo */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                Detalle de su reclamo:
              </h2>

              {/* Tipo de reclamo */}
              <div className='mb-6'>
                <label className='block text-sm font-medium text-gray-700 mb-3'>Tipo</label>
                <div className='flex gap-6'>
                  {CLAIM_TYPE_OPTIONS.map((option) => (
                    <label key={option.value} className='flex items-center'>
                      <input
                        type='radio'
                        name='tipoReclamo'
                        value={option.value}
                        checked={claimForm.tipoReclamo === option.value}
                        onChange={handleInputChange}
                        className='mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      />
                      <span className='text-sm text-gray-700'>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                <FormField
                  label='Pedido'
                  name='pedido'
                  value={claimForm.pedido}
                  onChange={handleInputChange}
                  placeholder='Número de pedido o referencia'
                />
              </div>

              <FormField
                label='Detalle del reclamo'
                name='detalleReclamo'
                type='textarea'
                value={claimForm.detalleReclamo}
                onChange={handleInputChange}
                placeholder='Describe detalladamente tu reclamo o queja...'
                rows={4}
                required
              />
            </div>

            {/* SECCIÓN 4: Datos del apoderado */}
            <div className='mb-8'>
              <h2 className='text-xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200'>
                Datos del apoderado:
              </h2>

              <div className='mb-6'>
                <FormField
                  label='Menor de Edad'
                  name='menorEdad'
                  type='select'
                  value={claimForm.menorEdad}
                  onChange={handleInputChange}
                  options={MINOR_AGE_OPTIONS}
                />
              </div>

              {claimForm.menorEdad === 'Si' && (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 p-6 bg-gray-50 rounded-lg border'>
                  <div className='md:col-span-3 mb-4'>
                    <h3 className='text-lg font-medium text-gray-800'>Información del Apoderado</h3>
                    <p className='text-sm text-gray-600'>Complete los datos del apoderado legal</p>
                  </div>

                  <FormField
                    label='Tipo de Documento Apoderado'
                    name='tipoDocumentoApoderado'
                    type='select'
                    value={claimForm.tipoDocumentoApoderado}
                    onChange={handleInputChange}
                    options={DOCUMENT_TYPE_OPTIONS}
                  />

                  <FormField
                    label='Documento Apoderado'
                    name='documentoApoderado'
                    value={claimForm.documentoApoderado}
                    onChange={handleInputChange}
                    placeholder='Número de documento del apoderado'
                    required
                  />

                  <FormField
                    label='Nombres'
                    name='nombreApoderado'
                    value={claimForm.nombreApoderado}
                    onChange={handleInputChange}
                    placeholder='Nombres del apoderado'
                  />

                  <FormField
                    label='Apellido Paterno'
                    name='apellidoPaternoApoderado'
                    value={claimForm.apellidoPaternoApoderado}
                    onChange={handleInputChange}
                    placeholder='Apellido paterno del apoderado'
                  />

                  <FormField
                    label='Apellido Materno'
                    name='apellidoMaternoApoderado'
                    value={claimForm.apellidoMaternoApoderado}
                    onChange={handleInputChange}
                    placeholder='Apellido materno del apoderado'
                  />

                  <FormField
                    label='Teléfono Fijo'
                    name='telefonoFijoApoderado'
                    type='tel'
                    value={claimForm.telefonoFijoApoderado}
                    onChange={handleInputChange}
                    placeholder='Teléfono fijo del apoderado'
                    required
                  />

                  <FormField
                    label='Teléfono Celular'
                    name='telefonoCelularApoderado'
                    type='tel'
                    value={claimForm.telefonoCelularApoderado}
                    onChange={handleInputChange}
                    placeholder='Teléfono celular del apoderado'
                    required
                  />

                  <FormField
                    label='E-mail'
                    name='emailApoderado'
                    type='email'
                    value={claimForm.emailApoderado}
                    onChange={handleInputChange}
                    placeholder='correo@ejemplo.com'
                  />
                </div>
              )}
            </div>

            {/* Botones de acción */}
            <div className='mt-8 flex justify-center space-x-4 pt-6 border-t border-gray-200'>
              <button
                type='button'
                onClick={handleCancel}
                disabled={claimSubmissionStatus === 'sending'}
                className='px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200'
              >
                Cancelar
              </button>
              <button
                type='submit'
                disabled={claimSubmissionStatus === 'sending' || !isFormValid}
                className='px-8 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center'
              >
                {claimSubmissionStatus === 'sending' ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    Enviando...
                  </>
                ) : (
                  'Enviar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal para éxito - NUEVO */}
      {claimSubmissionStatus === 'success' && (
        <FormSubmissionModal
          open={true}
          status={claimSubmissionStatus}
          onClose={() => {}} // No permitir cerrar durante éxito
          title='Reclamo Enviado Exitosamente'
          message='Tu reclamo ha sido registrado correctamente. Serás redirigido en unos segundos.'
        />
      )}

      {/* Modal para errores */}
      {claimSubmissionStatus === 'error' && (
        <FormSubmissionModal
          open={modalOpen}
          status={claimSubmissionStatus}
          onClose={handleModalClose}
          onRetry={handleRetry}
          title='Error al Enviar Reclamo'
        />
      )}

      {/* Toast notifications */}
      <ErrorSnackbar
        open={toastState.open}
        message={toastState.message}
        onClose={hideToast}
        severity={toastState.severity}
      />
    </div>
  );
}

export default memo(FormularioReclamo);
